import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Crown, X, Truck, MapPin, Clock } from "lucide-react";
import { IMG } from "../data/images";
import { SITE } from "../data/site";
import { useLang } from "../i18n";

const SESSION_KEY = "rt-delivery-notice";

export const DeliveryNotice = () => {
    const [open, setOpen] = useState(false);
    const { t } = useLang();

    useEffect(() => {
        let seen = false;
        try {
            seen = sessionStorage.getItem(SESSION_KEY) === "1";
        } catch {}
        if (seen) return;
        const id = setTimeout(() => setOpen(true), 1200);
        return () => clearTimeout(id);
    }, []);

    const close = () => {
        setOpen(false);
        try {
            sessionStorage.setItem(SESSION_KEY, "1");
        } catch {}
    };

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && close();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="delivery-notice-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="fixed inset-0 z-[95] bg-ink/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
                    onClick={close}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t("notice.title")}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94, y: 24 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 16 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-md gold-border bg-onyx rounded-3xl overflow-hidden shadow-[0_40px_120px_rgba(0,0,0,0.7)] max-h-[92svh] overflow-y-auto"
                    >
                        <div className="relative h-24 sm:h-40 overflow-hidden">
                            <img
                                src={IMG.butterChicken}
                                alt=""
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/40 to-ink/30" />
                            <button
                                data-testid="delivery-notice-close"
                                onClick={close}
                                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ink/70 backdrop-blur gold-border flex items-center justify-center text-cream hover:border-saffron/60 transition-colors duration-300"
                                aria-label="Close"
                            >
                                <X className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-onyx gold-border flex items-center justify-center shadow-lg">
                                <Crown className="w-5 h-5 text-saffron-bright" strokeWidth={1.5} />
                            </div>
                        </div>

                        <div className="px-6 sm:px-9 pt-8 pb-6 sm:pb-8 text-center">
                            <p className="text-[11px] uppercase tracking-[0.3em] text-brass font-semibold">
                                {t("notice.sorry")}
                            </p>
                            <h2 className="mt-2 sm:mt-3 font-serif text-xl sm:text-3xl text-cream leading-snug text-balance">
                                {t("notice.title")}
                            </h2>
                            <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-stone-400 font-light leading-relaxed">
                                {t("notice.body")}
                            </p>

                            <div className="mt-4 sm:mt-6 rounded-2xl border border-saffron/40 bg-saffron/10 p-4 sm:p-5 shadow-[0_0_40px_rgba(217,119,6,0.15)]">
                                <p className="text-[10px] uppercase tracking-[0.3em] text-saffron-glow font-semibold">
                                    {t("notice.goodNews")}
                                </p>
                                <p className="mt-1.5 sm:mt-2 flex items-center justify-center gap-2.5 font-serif text-lg sm:text-2xl text-cream">
                                    <Truck className="w-5 h-5 text-saffron-bright shrink-0" strokeWidth={1.5} />
                                    <span className="gold-text font-semibold">{t("notice.soonTitle")}</span>
                                </p>
                                <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-stone-300 font-light leading-relaxed">
                                    {t("notice.soonBody")}
                                </p>
                            </div>

                            <p className="mt-4 sm:mt-5 font-serif italic text-sm sm:text-base text-brass">{t("notice.thanks")}</p>

                            <div className="mt-4 sm:mt-5 flex items-center justify-center gap-x-5 gap-y-1.5 flex-wrap text-[11px] text-stone-500">
                                <span className="flex items-center gap-1.5">
                                    <MapPin className="w-3.5 h-3.5 text-saffron-bright" /> {SITE.addressLine1}, Kraków
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5 text-saffron-bright" /> {t("hero.hours")}
                                </span>
                            </div>

                            <motion.button
                                data-testid="delivery-notice-continue"
                                onClick={close}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.97 }}
                                className="mt-5 sm:mt-7 w-full rounded-full bg-saffron px-6 py-3.5 sm:py-4 text-sm font-semibold tracking-[0.12em] uppercase text-ink hover:bg-saffron-bright hover:shadow-[0_0_36px_rgba(217,119,6,0.35)] transition-[background-color,box-shadow] duration-300"
                            >
                                {t("notice.continue")}
                            </motion.button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
